import React, { useState, useEffect } from 'react';
import { Table, Tag, Button, Space, Progress, Modal, message, Card, Typography } from 'antd';
import { ReloadOutlined, EyeOutlined, DownloadOutlined, StopOutlined, DeleteOutlined } from '@ant-design/icons';
import { getTasks, getTask, cancelTask, deleteTaskLocal, TaskResponse } from '@/utils/taskManager';
import { formatUTCToLocal } from '@/utils/timeUtils';

const { Title } = Typography;

// 状态颜色映射
const statusColors = {
  pending: 'blue',
  queued: 'blue',
  running: 'processing',
  completed: 'success',
  failed: 'error',
  cancelled: 'default',
  timeout: 'orange'
};

// 状态文本映射
const statusTexts = {
  pending: '等待中',
  queued: '排队中',
  running: '运行中',
  completed: '已完成',
  failed: '失败',
  cancelled: '已取消',
  timeout: '超时'
};

interface TaskListProps {
  autoRefresh?: boolean; // 是否自动刷新
  refreshInterval?: number; // 刷新间隔（毫秒）
  onTaskSelected?: (task: TaskResponse) => void; // 选择任务回调
  serviceName?: string; // 过滤：服务名
  taskType?: string; // 过滤：任务类型
  handlerName?: string; // 过滤：处理器名称
}

const TaskList: React.FC<TaskListProps> = ({
  autoRefresh = true,
  refreshInterval = 5000,
  onTaskSelected,
  serviceName,
  taskType,
  handlerName
}) => {
  const [tasks, setTasks] = useState<TaskResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // 加载任务列表
  const fetchTasks = async () => {
    try {
      setLoading(true);
      setErrorMessage(null);
      const data = await getTasks(
        0,
        100,
        undefined,
        serviceName,
        taskType
      );
      // 如果指定了处理器名称，则进行前端过滤
      const filtered = handlerName
        ? data.filter(t => (t.handler_name || '').toLowerCase().includes((handlerName || '').toLowerCase()))
        : data;
      setTasks(filtered);
    } catch (error: any) {
      console.error('获取任务列表失败:', error);
      setErrorMessage(error.message || '获取任务列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 组件挂载时加载任务列表
  useEffect(() => {
    fetchTasks();
  }, []);

  // 自动刷新
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    
    // 只有当有任务正在运行或排队时才自动刷新
    if (autoRefresh && tasks.some(task => ['pending', 'queued', 'running'].includes(task.status))) {
      timer = setInterval(() => {
        fetchTasks();
      }, refreshInterval);
    }
    
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [autoRefresh, refreshInterval, tasks, serviceName, taskType, handlerName]);

  // 查看任务详情
  const viewTask = async (taskId: string) => {
    try {
      setLoading(true);
      const task = await getTask(taskId);
      
      if (onTaskSelected) {
        onTaskSelected(task);
      }
      
      setSelectedTaskId(taskId);
    } catch (error: any) {
      console.error('获取任务详情失败:', error);
      message.error('获取任务详情失败: ' + (error.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  const deleteTaskHandler = (taskId: string) => {
    Modal.confirm({
      title: '确认删除',
      content: '删除后无法恢复，确定要删除该任务吗？',
      okText: '删除',
      cancelText: '取消',
      okButtonProps: { danger: true },
      async onOk() {
        try {
          setLoading(true);
          await deleteTaskLocal(taskId);
          setTasks(prev => prev.filter(task => task.id !== taskId));
          if (selectedTaskId === taskId) {
            setSelectedTaskId(null);
          }
          message.success('任务已删除');
        } catch (error: any) {
          console.error('删除任务失败:', error);
          message.error('删除任务失败: ' + (error.message || '未知错误'));
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // 取消任务
  const cancelTaskHandler = async (taskId: string) => {
    Modal.confirm({
      title: '确认取消',
      content: '确定要取消该任务吗？',
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        try {
          setLoading(true);
          await cancelTask(taskId);
          message.success('任务已取消');
          fetchTasks();
        } catch (error: any) {
          console.error('取消任务失败:', error);
          message.error('取消任务失败: ' + (error.message || '未知错误'));
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // 表格列定义
  const columns = [
    {
      title: '任务名称',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => <a>{text}</a>
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={statusColors[status as keyof typeof statusColors]}>
          {statusTexts[status as keyof typeof statusTexts]}
        </Tag>
      )
    },
    {
      title: '进度',
      dataIndex: 'progress',
      key: 'progress',
      render: (progress: number) => <Progress percent={progress || 0} size="small" />
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text: string) => formatUTCToLocal(text)
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: TaskResponse) => (
        <Space size="small">
          <Button 
            type="primary"
            htmlType="button"
            icon={<EyeOutlined />} 
            onClick={() => viewTask(record.id)}
          >
            查看结果
          </Button>
          {['pending', 'queued', 'running'].includes(record.status) && (
            <Button 
              danger 
              htmlType="button"
              size="small" 
              icon={<StopOutlined />} 
              onClick={() => cancelTaskHandler(record.id)}
            >
              取消
            </Button>
          )}
          <Button
            danger
            htmlType="button"
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => deleteTaskHandler(record.id)}
          >
            删除
          </Button>
        </Space>
      )
    }
  ];

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={5}>预测任务列表</Title>
        <Button 
          type="primary" 
          icon={<ReloadOutlined />} 
          onClick={fetchTasks} 
          loading={loading}
        >
          刷新
        </Button>
      </div>
      
      {errorMessage && (
        <div style={{ color: 'red', marginBottom: 16 }}>
          错误: {errorMessage}
        </div>
      )}
      
      <Table 
        rowKey="id"
        columns={columns} 
        dataSource={tasks}
        loading={loading}
        pagination={{ pageSize: 10 }}
        rowClassName={(record) => record.id === selectedTaskId ? 'ant-table-row-selected' : ''}
      />
    </Card>
  );
};

export default TaskList; 